// For Functions v2 (Cloud Run), set REACT_APP_API_BASE_URL to your posApi Cloud Run URL (e.g. https://posapi-xxx.a.run.app).
// No hardcoded cloudfunctions.net — use env so it works with v2.
const BASE_URL = process.env.REACT_APP_API_BASE_URL || ''

/**
 * Fetch payment summary from Cloud Function (uses Admin SDK, bypasses security rules)
 * 
 * ✅ Secure: Uses Cloud Function with Admin SDK
 * ✅ No Firestore rules needed for anonymous users
 * ✅ YAP owns attribution
 * 
 * Returns: { paidQuantities: Map<itemId, paidQuantity>, totalPaid: number }
 */
async function fetchPaymentSummary(ticketId: string): Promise<{
  paidQuantities: Map<string, number>;
  totalPaid: number;
}> {
  // Guard: Check if ticketId is valid
  if (!ticketId) {
    console.warn("❌ fetchPaymentSummary: Missing ticketId, skipping query");
    return {
      paidQuantities: new Map<string, number>(),
      totalPaid: 0,
    };
  }

  console.log("🎟️ fetchPaymentSummary: ticketId used for query:", ticketId);

  const url = `${BASE_URL}/posApi/payments/summary?ticketId=${encodeURIComponent(ticketId)}`;
  const res = await fetch(url);

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to fetch payment summary' }));
    throw new Error(err.error || 'Failed to fetch payment summary');
  }

  const response = await res.json();
  const { paidQuantities: paidQtyObj, totalPaid } = response.data || {};

  // Convert object to Map
  const paidMap = new Map<string, number>();
  if (paidQtyObj) {
    Object.entries(paidQtyObj).forEach(([itemId, quantity]) => {
      paidMap.set(itemId, quantity as number);
    });
  }

  return {
    paidQuantities: paidMap,
    totalPaid: totalPaid || 0,
  };
}

/**
 * Fetch paid quantities by item for a ticket from YAP's Firestore (source of truth)
 * 
 * ✅ Uses Cloud Function (secure, bypasses Firestore rules)
 * ✅ YAP owns attribution
 * 
 * Returns: Map<itemId, paidQuantity>
 */
export async function fetchPaidQuantities(ticketId: string): Promise<Map<string, number>> {
  const summary = await fetchPaymentSummary(ticketId);
  return summary.paidQuantities;
}

/**
 * Compute total paid amount for a ticket from YAP payments
 * 
 * ✅ Uses Cloud Function (secure, bypasses Firestore rules)
 * ✅ Compute from YAP payments collection
 */
export async function fetchTotalPaid(ticketId: string): Promise<number> {
  const summary = await fetchPaymentSummary(ticketId);
  return summary.totalPaid;
}
